import React, { useMemo, useState } from "react";
import { CalendarRange, Wine, Check, ClipboardList, History, ChevronDown, ChevronUp } from "lucide-react";
import { Panel } from "../components/primitives";
import { useToast } from "../components/Toast";
import { useActiveTeam } from "../components/ActiveTeam";
import { useVersion } from "../components/Version";
import AvailabilityMobile from "./AvailabilityMobile";
import { api } from "../api";
import { fmtMatchTime } from "../utils/time";

const ELEVATED = ["ADMIN","PRESIDENT","VICE_PRESIDENT","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN"];
const STATUS_OPTIONS = [
  { value: "Available",     label: "Available",  color: "var(--green)" },
  { value: "Tentative",     label: "Tentative",  color: "var(--orange)" },
  { value: "Not Available", label: "Unavailable", color: "var(--red)" }
];

// §11.15 — Compute the upcoming weekend range (Saturday + Sunday).
// If today is Sat/Sun we return *this* weekend; otherwise the next one.
function getUpcomingWeekend() {
  const now = new Date();
  const day = now.getDay();
  const sat = new Date(now);
  if (day === 0)      sat.setDate(sat.getDate() - 1);
  else if (day !== 6) sat.setDate(sat.getDate() + (6 - day));
  sat.setHours(0, 0, 0, 0);
  const sun = new Date(sat); sun.setDate(sun.getDate() + 1);
  const iso = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  return { startIso: iso(sat), endIso: iso(sun), satDate: sat, sunDate: sun };
}

function prettyDate(d) {
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export default function Availability(props) {
  const { version } = useVersion();
  if (version === "v4") return <AvailabilityMobile {...props}/>;
  return <AvailabilityDesktop {...props}/>;
}

function AvailabilityDesktop({ state, reload, user, navigate }) {
  const toast = useToast();
  const { activeTeam, activeTeamId, isAllTeams } = useActiveTeam();
  const [busyKey, setBusyKey] = useState(null);
  // Admins/captains can record who actually showed up for past games.
  // Default off so the page still leads with "upcoming weekend" focus;
  // a toggle expands the past-matches list below when needed.
  const [showPast, setShowPast] = useState(false);

  const elevated = user?.roles?.some(r => ELEVATED.includes(r));

  const { startIso, endIso, satDate, sunDate } = useMemo(() => getUpcomingWeekend(), []);
  const todayIso = new Date().toISOString().slice(0, 10);

  // Phase 3 — driven by the global active team:
  //   - SUMMER_VOLLEYBALL  → next 2 upcoming matches (regardless of weekend)
  //   - any other team     → upcoming-weekend matches for that team
  //   - All Teams          → upcoming-weekend matches across every team
  const isSummerView = activeTeam?.code === "SUMMER_VOLLEYBALL";
  const matches = useMemo(() => {
    if (isSummerView) {
      return state.matches
        .filter(m => m.date >= todayIso && m.teamId === activeTeamId)
        .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time))
        .slice(0, 2);
    }
    return state.matches
      .filter(m => m.date >= startIso && m.date <= endIso)
      .filter(m => isAllTeams || m.teamId === activeTeamId)
      .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
  }, [state.matches, isSummerView, isAllTeams, activeTeamId, startIso, endIso, todayIso]);

  // Recent past matches (last 10 within active team scope) — only
  // rendered when the admin clicks "Show past matches". Used to
  // retroactively record actual attendance for completed games.
  const pastMatches = useMemo(() => {
    return state.matches
      .filter(m => m.date < todayIso)
      .filter(m => isAllTeams || m.teamId === activeTeamId)
      .sort((a, b) => (b.date + (b.time || "")).localeCompare(a.date + (a.time || "")))
      .slice(0, 10);
  }, [state.matches, isAllTeams, activeTeamId, todayIso]);

  async function setStatus(matchId, userId, status) {
    const key = `${matchId}:${userId}`;
    const isSelf = userId === user.id;
    if (!isSelf && !elevated) return;

    setBusyKey(key);
    try {
      await api.setAvailability(matchId, userId, { status, comments: "" });
      await reload();
    } catch (err) { toast.error(err.message || "Couldn't update availability."); }
    finally { setBusyKey(null); }
  }

  // Phase 3 — drinks volunteer toggle. Updates only the VolunteerDrinks
  // flag on PlayerAvailability (preserving existing status / comments).
  async function setDrinks(matchId, userId, on) {
    const key = `drinks:${matchId}:${userId}`;
    const isSelf = userId === user.id;
    if (!isSelf && !elevated) return;
    setBusyKey(key);
    try {
      await api.setAvailability(matchId, userId, { volunteerDrinks: !!on });
      await reload();
    } catch (err) { toast.error(err.message || "Couldn't update volunteer status."); }
    finally { setBusyKey(null); }
  }

  // Set of userIds who have already volunteered for drinks in any match in
  // this season's availability data. Used to flag players going forward so
  // a single member doesn't keep getting tapped.
  const drinksDoneByUser = useMemo(() => {
    const s = new Set();
    for (const a of (state.availability || [])) {
      if (a.volunteerDrinks) s.add(a.userId);
    }
    return s;
  }, [state.availability]);

  return (
    <div className="stack">
      <div className="builderTop">
        <div>
          <p>Match Availability · {elevated ? "Team Roster View" : "Your Status"}</p>
          <h2>Availability — {isSummerView ? "Next 2 Summer Volleyball Games" : "Upcoming Weekend"}</h2>
        </div>
      </div>

      <Panel title={
        <>
          <CalendarRange size={18}/>
          {isSummerView
            ? " Next two upcoming Summer Volleyball games"
            : ` ${prettyDate(satDate)} – ${prettyDate(sunDate)}`}
        </>
      }>
        <div style={{display:"flex", alignItems:"center", gap:10, flexWrap:"wrap"}}>
          <span style={{color:"var(--muted)", fontSize:12, fontWeight:700, letterSpacing:"0.14em", textTransform:"uppercase"}}>
            {isAllTeams ? "All Teams" : (activeTeam?.name || "Team")}
            {activeTeam?.sport && !isAllTeams && ` · ${activeTeam.sport}`}
          </span>
          <span style={{marginLeft:"auto", color:"var(--muted)", fontSize:12}}>
            {matches.length} match{matches.length !== 1 ? "es" : ""}{isSummerView ? "" : " this weekend"}
          </span>
        </div>
      </Panel>

      {matches.length === 0 && (
        <Panel title={isSummerView ? "No Summer Volleyball matches scheduled" : "No matches this weekend"}>
          <p style={{color:"var(--muted)", textAlign:"center", padding:"20px 0"}}>
            {isSummerView
              ? "No upcoming Summer Volleyball games in the schedule. Admin can add them in Schedules."
              : `No matches scheduled for ${prettyDate(satDate)} – ${prettyDate(sunDate)}${!isAllTeams ? ` for ${activeTeam?.name}` : ""}.`}
          </p>
        </Panel>
      )}

      {matches.map(m => renderMatchPanel(m, false, {
        state, user, navigate, elevated, busyKey, setStatus, setDrinks, drinksDoneByUser
      }))}

      {/* Past matches — admin-only retroactive recording of attendance.
          Renders below the upcoming weekend so the page still leads
          with what's coming next. Hidden by default; admins toggle on. */}
      {elevated && (
        <Panel title={
          <>
            <History size={16}/> Past Matches
            <span style={{marginLeft:8, fontSize:11, color:"var(--muted)", fontWeight:400, letterSpacing:0, textTransform:"none"}}>
              Record actual attendance retroactively
            </span>
            <button
              type="button"
              className={showPast ? "btn" : "dark"}
              style={{marginLeft:"auto", padding:"6px 14px", fontSize:12}}
              onClick={() => setShowPast(v => !v)}
            >
              {showPast
                ? <><ChevronUp size={14}/> Hide</>
                : <><ChevronDown size={14}/> Show last {pastMatches.length} match{pastMatches.length === 1 ? "" : "es"}</>}
            </button>
          </>
        }>
          {!showPast ? (
            <p style={{color:"var(--muted)", textAlign:"center", padding:"4px 0", fontSize:13}}>
              {pastMatches.length === 0
                ? "No past matches in scope."
                : "Toggle above to expand. Use this to log who actually showed up vs RSVP'd."}
            </p>
          ) : pastMatches.length === 0 ? (
            <p style={{color:"var(--muted)", textAlign:"center", padding:"16px"}}>No past matches in scope.</p>
          ) : (
            <p style={{color:"var(--muted)", fontSize:12, margin:"0 0 4px"}}>
              Showing the last {pastMatches.length} match{pastMatches.length === 1 ? "" : "es"} in date-descending order.
            </p>
          )}
        </Panel>
      )}

      {elevated && showPast && pastMatches.map(m => renderMatchPanel(m, true, {
        state, user, navigate, elevated, busyKey, setStatus, setDrinks, drinksDoneByUser
      }))}
    </div>
  );
}

// Renders a single match's availability table. Used for both the
// upcoming-weekend list and (when admins toggle it on) past matches.
// `isPast` flips the panel title to "Past Match" and tightens edit
// permissions so players can't retroactively change their RSVPs.
function renderMatchPanel(m, isPast, ctx) {
  const { state, user, navigate, elevated, busyKey, setStatus, setDrinks, drinksDoneByUser } = ctx;
  const team   = state.teams.find(t => t.id === m.teamId);
  const roster = (team?.players || [])
    .map(uid => state.members.find(u => u.id === uid))
    .filter(Boolean);
  return (
    <Panel
      key={m.id}
      title={
        <>
          {isPast && <span className="tag" style={{marginRight:8, color:"var(--muted)", borderColor:"var(--muted)", fontSize:10}}>PAST</span>}
          {team?.name || "Pitt Gabruz"} vs {m.opponent}
          <span style={{marginLeft:10, color:"var(--muted)", fontSize:13, fontWeight:400, letterSpacing:0, textTransform:"none", fontFamily:"var(--body)"}}>
            {m.sport} · {m.date} · {m.time ? `${fmtMatchTime(m.time)} ET` : "TBD"} · {m.venue}
          </span>
          {navigate && !isPast && (
            <button
              type="button"
              className="dark"
              style={{marginLeft:12, padding:"4px 12px", fontSize:11, fontWeight:600}}
              onClick={() => navigate("squad")}
              title="Open the Squads page for this match"
            ><ClipboardList size={12}/> View squad</button>
          )}
        </>
      }
    >
      <table>
        <thead>
          <tr><th>Player</th><th>Status</th><th>Set Status</th><th>Drinks</th></tr>
        </thead>
        <tbody>
          {roster.length === 0 && (
            <tr><td colSpan={4} style={{color:"var(--muted)", textAlign:"center", padding:"16px"}}>No players on this team.</td></tr>
          )}
          {roster.map(p => {
            const av = state.availability.find(a => a.matchId === m.id && a.userId === p.id);
            const key = `${m.id}:${p.id}`;
            const isSelf = p.id === user.id;
            // Past matches — only elevated users can rewrite history.
            // Players can't retroactively change their own RSVPs.
            const canEdit = isPast ? elevated : (isSelf || elevated);
            const drinksHere = !!av?.volunteerDrinks;
            const drinksDoneAlready = drinksDoneByUser.has(p.id);
            const showDoneBadge = drinksDoneAlready && !drinksHere;
            const drinksKey = `drinks:${m.id}:${p.id}`;
            const drinksBusy = busyKey === drinksKey;
            return (
              <tr key={p.id} style={isSelf ? {background:"rgba(247,201,72,0.06)"} : null}>
                <td>
                  <b>{p.name}</b>
                  {isSelf && <span className="tag" style={{marginLeft:8}}>You</span>}
                  {p.id === team?.captainId && <span className="tag" style={{marginLeft:8}}>Captain</span>}
                </td>
                <td>
                  {av ? (
                    <span className="tag" style={{
                      color: STATUS_OPTIONS.find(s => s.value === av.status)?.color,
                      borderColor: STATUS_OPTIONS.find(s => s.value === av.status)?.color
                    }}>{av.status}</span>
                  ) : (
                    <span style={{color:"var(--muted)"}}>No response</span>
                  )}
                </td>
                <td style={{display:"flex", gap:6, flexWrap:"wrap"}}>
                  {STATUS_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      className={av?.status === opt.value ? "btn" : "dark"}
                      style={{padding:"4px 10px", fontSize:12}}
                      disabled={!canEdit || busyKey === key}
                      onClick={() => setStatus(m.id, p.id, opt.value)}
                    >{opt.label}</button>
                  ))}
                </td>
                <td>
                  {drinksHere ? (
                    <span style={{display:"inline-flex", alignItems:"center", gap:6}}>
                      <span className="tag" style={{color:"var(--green)", borderColor:"var(--green)"}}>
                        <Check size={11} style={{verticalAlign:"middle", marginRight:3}}/> Volunteered
                      </span>
                      {canEdit && (
                        <button
                          type="button"
                          className="dark"
                          style={{padding:"3px 8px", fontSize:11}}
                          disabled={drinksBusy}
                          onClick={() => setDrinks(m.id, p.id, false)}
                          title="Undo drinks volunteer"
                        >Undo</button>
                      )}
                    </span>
                  ) : (
                    <span style={{display:"inline-flex", alignItems:"center", gap:6, flexWrap:"wrap"}}>
                      <button
                        type="button"
                        className="dark"
                        style={{padding:"4px 10px", fontSize:12, display:"inline-flex", alignItems:"center", gap:6}}
                        disabled={!canEdit || drinksBusy}
                        onClick={() => setDrinks(m.id, p.id, true)}
                      ><Wine size={13}/> Volunteer</button>
                      {showDoneBadge && (
                        <span
                          className="tag"
                          style={{color:"var(--orange)", borderColor:"var(--orange)", fontSize:10}}
                          title="Already volunteered for drinks earlier this season"
                        >Done this season</span>
                      )}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <small style={{display:"block", marginTop:8, color:"var(--muted)"}}>
        {isPast
          ? "Past match — only admins / captains can rewrite RSVPs here. Useful for recording who actually showed up."
          : (elevated
              ? "You can set availability for any player on this team."
              : "You can only set your own status. Ask a captain or admin to change others.")}
      </small>
    </Panel>
  );
}
